import { Rocket } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import Matter from "matter-js";
import { FadeIn } from "./ui";

const keywords = [
  { text: "Branding", color: "bg-brand-accent text-brand-light border-transparent" },
  { text: "Posicionamento", color: "bg-brand-dark text-brand-light border-transparent" },
  { text: "Gestão de Redes", color: "bg-brand-light text-brand-dark border-brand-dark/20" },
  { text: "Lançamentos Digitais", color: "bg-brand-accent text-brand-light border-transparent" },
  { text: "Automação & IA", color: "bg-brand-dark text-brand-light border-transparent" },
  { text: "Consultoria Estratégica", color: "bg-brand-light text-brand-dark border-brand-dark/20" },
  { text: "Tráfego Pago", color: "bg-brand-accent text-brand-light border-transparent" },
  { text: "Design Gráfico", color: "bg-brand-dark text-brand-light border-transparent" },
  { text: "Copywriting", color: "bg-brand-light text-brand-dark border-brand-dark/20" },
  { text: "Inovação", color: "bg-brand-accent text-brand-light border-transparent" },
  { text: "Resultados", color: "bg-brand-dark text-brand-light border-transparent" },
  { text: "Performance", color: "bg-brand-light text-brand-dark border-brand-dark/20" },
  { text: "Identidade Visual", color: "bg-brand-accent text-brand-light border-transparent" },
  { text: "Growth", color: "bg-brand-dark text-brand-light border-transparent" },
  { text: "Conteúdo", color: "bg-brand-light text-brand-dark border-brand-dark/20" },
  { text: "Web Design", color: "bg-brand-accent text-brand-light border-transparent" },
  { text: "Conversão", color: "bg-brand-dark text-brand-light border-transparent" },
  { text: "SEO", color: "bg-brand-light text-brand-dark border-brand-dark/20" },
];

export function PhysicsBalloons() {
  const containerRef = useRef<HTMLDivElement>(null);
  const itemsRef = useRef<(HTMLDivElement | null)[]>([]);
  const [isVisible, setIsVisible] = useState(false);

  // Don't run the physics simulation until this section actually scrolls into
  // view — it used to start at mount time, burning CPU on every page load
  // (including on other routes' initial paint) for content below the fold.
  useEffect(() => {
    const el = containerRef.current;
    if (!el || isVisible) return;
    const observer = new IntersectionObserver(
      ([entry]) => entry.isIntersecting && setIsVisible(true),
      { rootMargin: "200px" }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [isVisible]);

  useEffect(() => {
    if (!isVisible || !containerRef.current) return;

    const Engine = Matter.Engine,
      Runner = Matter.Runner,
      MouseConstraint = Matter.MouseConstraint,
      Mouse = Matter.Mouse,
      World = Matter.World,
      Bodies = Matter.Bodies;

    const engine = Engine.create();
    const world = engine.world;

    const width = containerRef.current.clientWidth;
    const height = containerRef.current.clientHeight;

    // Boundaries
    const wallOptions = { isStatic: true, render: { visible: false } };
    const ground = Bodies.rectangle(width / 2, height + 50, width * 2, 100, wallOptions);
    const leftWall = Bodies.rectangle(-50, height / 2, 100, height * 2, wallOptions);
    const rightWall = Bodies.rectangle(width + 50, height / 2, 100, height * 2, wallOptions);
    const ceiling = Bodies.rectangle(width / 2, -500, width * 2, 100, wallOptions);

    World.add(world, [ground, leftWall, rightWall, ceiling]);

    // Create bodies based on DOM elements
    const bodies: Matter.Body[] = [];

    itemsRef.current.forEach((elem) => {
      if (!elem) return;
      const w = elem.offsetWidth;
      const h = elem.offsetHeight;

      const safeWidth = Math.max(0, width - w);
      const x = Math.random() * safeWidth + w / 2;
      const y = -Math.random() * 500 - 100;

      const body = Bodies.rectangle(x, y, w, h, {
        chamfer: { radius: h / 2 },
        restitution: 0.6,
        friction: 0.1,
        density: 0.001,
        render: { visible: false }
      });

      bodies.push(body);
    });

    World.add(world, bodies);

    const mouse = Mouse.create(containerRef.current);
    const mouseConstraint = MouseConstraint.create(engine, {
      mouse: mouse,
      constraint: {
        stiffness: 0.2,
        render: { visible: false }
      }
    });

    World.add(world, mouseConstraint);

    const runner = Runner.create();
    Runner.run(runner, engine);

    // Explosion interaction
    const rocketBtn = document.getElementById('rocket-button');
    const handleExplosion = () => {
      if (!containerRef.current) return;
      const centerX = containerRef.current.clientWidth / 2;
      const centerY = containerRef.current.clientHeight / 2;

      bodies.forEach(body => {
        const dx = body.position.x - centerX;
        const dy = body.position.y - centerY;
        const distance = Math.sqrt(dx * dx + dy * dy) || 1;

        const dirX = dx / distance;
        const dirY = dy / distance;

        const speed = 15 + Math.random() * 10;

        Matter.Body.setVelocity(body, {
          x: body.velocity.x + dirX * speed,
          y: body.velocity.y + dirY * speed - 5
        });

        Matter.Body.setAngularVelocity(body, (Math.random() - 0.5) * 0.4);
      });
    };
    rocketBtn?.addEventListener('click', handleExplosion);

    let animationFrame: number;
    const update = () => {
      bodies.forEach((body, i) => {
        const elem = itemsRef.current[i];
        if (elem) {
          elem.style.transform = `translate(${body.position.x - elem.offsetWidth / 2}px, ${body.position.y - elem.offsetHeight / 2}px) rotate(${body.angle}rad)`;
        }
      });
      animationFrame = requestAnimationFrame(update);
    };
    update();

    const handleResize = () => {
      if (!containerRef.current) return;
      const newWidth = containerRef.current.clientWidth;
      const newHeight = containerRef.current.clientHeight;

      Matter.Body.setPosition(ground, { x: newWidth / 2, y: newHeight + 50 });
      Matter.Body.setPosition(rightWall, { x: newWidth + 50, y: newHeight / 2 });
    };

    window.addEventListener('resize', handleResize);

    return () => {
      rocketBtn?.removeEventListener('click', handleExplosion);
      window.removeEventListener('resize', handleResize);
      cancelAnimationFrame(animationFrame);
      Runner.stop(runner);
      Engine.clear(engine);
    };
  }, [isVisible]);

  return (
    <FadeIn delay={0.4}>
      <div
        ref={containerRef}
        className="relative w-full h-[450px] md:h-[400px] mt-16 rounded-3xl overflow-hidden bg-[#0a0a0a] border border-brand-light/10 shadow-inner group cursor-grab active:cursor-grabbing"
      >
        {/* Blueprint Grid */}
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff05_1px,transparent_1px),linear-gradient(to_bottom,#ffffff05_1px,transparent_1px)] bg-[size:40px_40px] z-0"></div>

        {/* Central System / Rocket */}
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-0 overflow-hidden">
          {/* Glowing background orb */}
          <div className="absolute w-64 h-64 bg-brand-accent/20 rounded-full blur-[80px]"></div>

          {/* Axis lines */}
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-full h-[1px] bg-gradient-to-r from-transparent via-brand-accent/20 to-transparent"></div>
            <div className="absolute h-full w-[1px] bg-gradient-to-b from-transparent via-brand-accent/20 to-transparent"></div>
          </div>

          {/* Concentric connection rings */}
          <div className="absolute w-[800px] h-[800px] border border-brand-light/5 rounded-full border-dashed animate-[spin_120s_linear_infinite]"></div>
          <div className="absolute w-[500px] h-[500px] border border-brand-light/5 rounded-full border-dashed animate-[spin_90s_linear_infinite_reverse]"></div>
          <div className="absolute w-[250px] h-[250px] border border-brand-accent/20 rounded-full border-dashed animate-[spin_60s_linear_infinite]"></div>

        </div>

        {/* Center Node (Rocket) - Above Canvas */}
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-30">
          <div className="relative flex items-center justify-center">
            <div className="absolute w-20 h-20 bg-brand-accent/20 rounded-full animate-ping pointer-events-none"></div>
            <button
              id="rocket-button"
              className="relative bg-brand-dark p-4 rounded-full border border-brand-accent shadow-[0_0_30px_rgba(242,125,38,0.3)] pointer-events-auto cursor-pointer hover:scale-110 hover:shadow-[0_0_50px_rgba(242,125,38,0.6)] transition-all active:scale-95 group/btn"
              title="Impulsionar Ecossistema"
            >
              <Rocket className="w-8 h-8 text-brand-accent -translate-y-0.5 translate-x-0.5 group-active/btn:-translate-y-2 transition-transform" />
            </button>
          </div>
        </div>

        {/* Label */}
        <div className="absolute bottom-6 left-0 right-0 flex justify-center pointer-events-none z-30">
          <div className="flex items-center gap-3 px-5 py-2.5 rounded-full bg-brand-dark/90 border border-brand-light/10 backdrop-blur-md shadow-xl">
            <div className="w-2 h-2 rounded-full bg-brand-accent animate-pulse"></div>
            <span className="text-brand-light/80 text-xs font-bold uppercase tracking-widest">
              Ecossistema Integrado
            </span>
          </div>
        </div>

        {keywords.map((keyword, i) => (
          <div
            key={i}
            ref={el => { itemsRef.current[i] = el; }}
            className={`absolute top-0 left-0 px-4 py-2 md:px-6 md:py-3 font-bold rounded-full whitespace-nowrap pointer-events-none z-30 shadow-lg border text-xs md:text-base select-none ${keyword.color}`}
            style={{ transform: 'translate(-1000px, -1000px)' }}
          >
            {keyword.text}
          </div>
        ))}
      </div>
    </FadeIn>
  );
}
